import {useCallback} from 'react'

import {fromPairs} from 'lodash'
import {useHits} from 'react-instantsearch'

import {useEvents} from '@festi/common/contexts'
import {useCommonContext} from '@festi/common/contexts/common'

import FacetItem from 'src/components/filters/Facet'
import {
  FacetTreeItem,
  sortFacetOptions,
  convertToFacetTree,
  HierarchicalFacetOption,
} from 'src/components/search/redesign/utilites/facets'
import {RefinementListItem} from 'src/components/search/redesign/utilites/refinement'

interface Props {
  items: RefinementListItem[]
  attribute?: string
  refine: (value: string) => void
}

export default function HierarchicalRefinement({
  items,
  attribute = 'categories',
  refine,
}: Props) {
  const {results} = useHits()
  const {categories} = useCommonContext()
  const {sendFilterClickedEvent} = useEvents()
  const facetOrderValues = results?.renderingContent?.facetOrdering?.values

  const categoryMap = fromPairs(categories?.map((c) => [c.id, c]))

  const facetOptions: HierarchicalFacetOption[] = items
    ?.map(({value, count}) => {
      const id = parseInt(value, 10)
      const cat = categoryMap[id]
      if (cat?.name) {
        return {
          value: id,
          label: cat.name,
          count: count,
          active: !!items.find((i) => i.value === value)?.isRefined,
          parent: cat.parent,
        }
      }
      return null
    })
    ?.filter((cat) => cat)

  const sortedOptions = sortFacetOptions(
    attribute,
    facetOptions,
    facetOrderValues,
  )

  const facetTree = convertToFacetTree(
    sortedOptions as HierarchicalFacetOption[],
  )
  const facetTreeValues = Array.from(facetTree.values()).filter(
    (p) => !p.parent,
  )

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      const checked = e.target.checked

      refine(value)

      if (checked) {
        sendFilterClickedEvent(
          'clickedFilters',
          'Filter clicked',
          `${attribute}:${value}`,
        )
      }
    },
    [attribute, refine, sendFilterClickedEvent],
  )

  return (
    <div>
      {facetTreeValues?.map((o: FacetTreeItem) => {
        return (
          <FacetItem key={o.value} option={o} onChange={onChange} level={0} />
        )
      })}
    </div>
  )
}
